/**
 * CloudGenie brand mark — inline SVG so it inlines into the bundle and
 * never fails to load. Cloud silhouette with a spark of intelligence
 * cutting across it. Tuned to look good at 20–48px.
 */
import clsx from 'clsx';

type ProductAccent = 'finops' | 'studio' | 'id';

interface LogoProps {
  product?: ProductAccent;
  size?: number;
  className?: string;
  withWordmark?: boolean;
}

/** Product-specific accent stop for the gradient's second color. */
const accentStop: Record<ProductAccent, string> = {
  finops: '#22d3ee', // cyan-400 — cost/analytics
  studio: '#f472b6', // pink-400 — creative/IaC
  id:     '#a78bfa', // violet-400 — identity/admin
};

export function Logo({ product = 'id', size = 24, className, withWordmark = false }: LogoProps) {
  const gradientId = `cg-lg-${product}`;
  const sparkId = `cg-spark-${product}`;
  return (
    <span className={clsx('inline-flex items-center gap-2', className)} aria-label="CloudGenie">
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        role="img"
        aria-hidden={withWordmark ? undefined : 'true'}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#6366f1" />       {/* indigo-500 */}
            <stop offset="100%" stopColor={accentStop[product]} />
          </linearGradient>
          <linearGradient id={sparkId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#fbbf24" />       {/* amber-400 */}
            <stop offset="100%" stopColor="#fde68a" />     {/* amber-200 */}
          </linearGradient>
        </defs>
        {/* Cloud body — soft rounded shape */}
        <path
          d="M14 32c-4.4 0-8-3.6-8-8s3.6-8 8-8c.6 0 1.2.1 1.7.2C17 12.3 20.6 10 25 10c5.5 0 10 4.5 10 10 0 .3 0 .6-.1.9 3.1.5 5.5 3.2 5.5 6.5 0 3.6-2.9 6.6-6.6 6.6H14z"
          fill={`url(#${gradientId})`}
          opacity={0.95}
        />
        {/* Lightning spark — the "intelligence" mark */}
        <path
          d="M25 15L18 27h6l-2 8 9-13h-6l2-7z"
          fill={`url(#${sparkId})`}
          stroke="#fef3c7"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
      </svg>
      {withWordmark && (
        <span className="font-semibold tracking-tight text-[15px]">
          Cloud<span style={{ color: accentStop[product] }}>Genie</span>
        </span>
      )}
    </span>
  );
}

export default Logo;
